/* verticalBoxplotRenderer.ts */

/* d3.js */
import * as d3 from "d3";

/* Types */
import { SVGSelection } from "src/shared/types/d3";

/* Helper */
import {
  createGradients,
  createViewBox,
  drawVerticalBoxplots,
} from "src/components/charts/vertical-boxplot/helper";

/* Constants */
import { ChartType } from "src/shared/constants/charts/Chart";

/* Styles */
import { basicStyles } from "src/shared/styles/Charts";

/* Interfaces */
import { BoxplotProps } from "src/shared/interfaces/charts/Boxplot";
import { ChartDimensionsProps } from "src/shared/interfaces/charts/BaseChart";
import { ChartTooltipRef } from "src/shared/interfaces/ChartTooltip";
import { computePlotHeightWidth } from "src/utils/helper/charts/helper";

export const createVerticaBoxplot = (
  svg: SVGSelection,
  tooltipRef: React.RefObject<ChartTooltipRef>,
  data: BoxplotProps[],
  dimensions: ChartDimensionsProps
) => {
  const { margin, height, width } = computePlotHeightWidth(
    dimensions,
    { top: 21, right: 32, bottom: 21, left: 32 }
  );

  createViewBox(svg, dimensions);

  const categories = data.map((d) => d.name ?? "");

  const allValues = data.flatMap((d) => d.y ?? []);
  const rawMin = d3.min(allValues) ?? 0;
  const rawMax = d3.max(allValues) ?? 0;

  const padding = (rawMax - rawMin) * 0.2;

  const lowerBound =
    Math.floor((rawMin - padding) / 10) * 10;
  const upperBound =
    Math.ceil((rawMax + padding) / 10) * 10;

  const xScale = d3
    .scaleLinear()
    .domain([lowerBound, upperBound])
    .range([0, width]);

  const yScale = d3
    .scaleBand()
    .domain(categories)
    .range([0, height])
    .padding(0.5);

  const g = svg
    .append("g")
    .attr(
      "transform",
      `translate(${margin.left},${margin.top})`
    );

  const ticks = xScale.ticks(
    (upperBound - lowerBound) / 10
  );

  // Grid lines
  g.append("g")
    .selectAll("line.grid-line")
    .data(ticks)
    .enter()
    .append("line")
    .attr("class", "grid-line")
    .attr("x1", (d) => xScale(d))
    .attr("x2", (d) => xScale(d))
    .attr("y1", 21)
    .attr("y2", height - 21)
    .attr("stroke", "#ccc")
    .attr("stroke-dasharray", "3,3")
    .attr("stroke-width", 1);

  // Axis
  g.append("g")
    .attr("transform", `translate(0,${height})`)
    .call(
      d3.axisBottom(xScale).tickValues(ticks).tickSize(0)
    )
    .call((g) => g.select(".domain").remove())
    .selectAll("text")
    .attr("y", 0)
    .style("font-size", basicStyles.font.size)
    .style("fill", basicStyles.font.color);

  createGradients(svg, categories, ChartType.BOX);
  drawVerticalBoxplots(
    svg,
    tooltipRef,
    g,
    data,
    xScale,
    yScale
  );
};
